import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Mail, Lock, User, Phone, ArrowRight, Loader2 } from 'lucide-react';
import { PasswordStrengthIndicator, validatePassword } from '@/components/PasswordStrengthIndicator';

function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    mobile: '',
    password: '',
    role: 'account_holder',
    otp_enabled: false,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate password strength
    if (!formData.otp_enabled && !validatePassword(formData.password)) {
      toast.error('Please meet all password requirements');
      return;
    }
    
    setLoading(true);

    try {
      await register(formData);
      toast.success('Registration successful!');
      navigate('/');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 -right-40 w-80 h-80 bg-emerald-200 rounded-full opacity-20 blur-3xl"></div>
        <div className="absolute -bottom-20 -left-40 w-80 h-80 bg-teal-200 rounded-full opacity-20 blur-3xl"></div>
      </div>

      <div className="w-full max-w-6xl grid md:grid-cols-2 gap-12 items-center relative z-10">
        {/* Left side - Branding */}
        <div className="space-y-8 fade-in">
          <div className="space-y-4">
            <div className="flex items-center gap-4">
              <img
                src="https://customer-assets.emergentagent.com/job_scalemicrofin/artifacts/zwx3wriv_scaleMicroFin-icon%27png.png"
                alt="ScaleMicroFIn Logo"
                className="h-20 w-20 drop-shadow-lg"
              />
              <div>
                <h1 className="text-5xl sm:text-6xl font-bold gradient-text leading-tight">
                  ScaleMicroFIn
                </h1>
                <p className="text-sm text-emerald-600 font-medium mt-1">Join Our Community</p>
              </div>
            </div>
          </div>
          
          <p className="text-lg text-gray-700 max-w-md leading-relaxed">
            Join our community and start managing your finances with <span className="font-semibold text-emerald-600">transparency</span> and <span className="font-semibold text-emerald-600">trust</span>.
          </p>

          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-6 rounded-2xl space-y-3">
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
              <svg className="w-5 h-5 text-emerald-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              Why Join Us?
            </h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-start gap-2">
                <span className="text-emerald-600">•</span>
                <span>Free forever for basic features</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-600">•</span>
                <span>Bank-grade security for your data</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-emerald-600">•</span>
                <span>24/7 community support</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Right side - Register form */}
        <Card className="glass card-hover" data-testid="register-card">
          <CardHeader>
            <CardTitle className="text-2xl" data-testid="register-title">Register</CardTitle>
            <CardDescription>Create a new account</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Full Name</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Your full name"
                    className="pl-10"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    id="email"
                    type="email"
                    placeholder="your@email.com"
                    className="pl-10"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="mobile">Mobile Number</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
                  <Input
                    id="mobile"
                    type="tel"
                    placeholder="+91 1234567890"
                    className="pl-10"
                    value={formData.mobile}
                    onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                    data-testid="mobile-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                  <SelectTrigger data-testid="role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="account_holder">Account Holder</SelectItem>
                    <SelectItem value="mandali_manager">Manager</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-semibold text-gray-700">
                  Password
                </Label>
                <div className="relative">
                  <Lock className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={20} />
                  <Input
                    id="password"
                    type="password"
                    placeholder="Create a strong password"
                    className="pl-12 h-12 form-input text-base"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required={!formData.otp_enabled}
                    disabled={formData.otp_enabled}
                    data-testid="password-input"
                  />
                </div>
                {!formData.otp_enabled && <PasswordStrengthIndicator password={formData.password} />}
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="otp-enabled"
                  checked={formData.otp_enabled}
                  onCheckedChange={(checked) => setFormData({ ...formData, otp_enabled: checked })}
                  data-testid="otp-enabled-checkbox"
                />
                <Label htmlFor="otp-enabled" className="text-sm cursor-pointer">
                  Enable OTP-based login (no password required)
                </Label>
              </div>

              <Button
                type="submit"
                className="w-full bg-teal-600 hover:bg-teal-700"
                disabled={loading}
                data-testid="register-button"
              >
                {loading ? (
                  <Loader2 className="animate-spin" size={18} />
                ) : (
                  <>
                    Register
                    <ArrowRight className="ml-2" size={18} />
                  </>
                )}
              </Button>

              <div className="text-center text-sm text-gray-600">
                Already have an account?{' '}
                <Link to="/login" className="text-teal-600 hover:text-teal-700 font-medium" data-testid="login-link">
                  Login here
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default Register;
